import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Stack
} from '@mui/material';
import { 
  Assessment as AssessmentIcon, 
  AccountBalance as AccountBalanceIcon, 
  Receipt as ReceiptIcon,
  PendingActions as PendingActionsIcon,
  AccountBalanceWallet as DisponibilidadesIcon
} from '@mui/icons-material';

const TesoreriaInformes: React.FC = () => {
  const navigate = useNavigate();

  const informes = [
    {
      title: 'Disponibilidades',
      description: 'Visualiza los saldos disponibles de las carteras por cuenta corriente.',
      icon: DisponibilidadesIcon,
      path: '../disponibilidades',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Movimientos de Carteras',
      description: 'Visualiza el detalle de los movimientos que componen el saldo de las carteras.',
      icon: AccountBalanceIcon,
      path: 'movimientos-carteras',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Cheques de Tercero',
      description: 'Visualiza los cheques recibidos de terceros.',
      icon: AssessmentIcon,
      path: 'cheques-tercero',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Cheques de Terceros en Cartera',
      description: 'Visualiza los cheques de terceros que están en cartera.',
      icon: AccountBalanceIcon,
      path: 'cheques-terceros-cartera',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Cheques Propios',
      description: 'Visualiza los cheques propios emitidos.',
      icon: ReceiptIcon,
      path: 'cheques-propios',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Cheques Propios Pendientes de Acreditar',
      description: 'Visualiza los cheques propios que están pendientes de acreditar.',
      icon: PendingActionsIcon,
      path: 'cheques-propios-pendientes-acreditar',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                mb: 1
              }}
            >
              Informes de Tesorería
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: '0.95rem' }}
            >
              Selecciona un informe para visualizar información detallada
            </Typography>
          </Box>

          {/* Grid de tarjetas de informes */}
          <Grid container spacing={2.5}>
            {informes.map((informe) => {
              const IconComponent = informe.icon;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={informe.path}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      border: '1px solid #e0e0e0',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      height: '100%',
                      minHeight: { xs: 180, sm: 195 },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                        borderColor: informe.color,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(informe.path)}
                      sx={{
                        p: 0,
                        height: '100%',
                        alignItems: 'stretch',
                        '&:hover': {
                          '& .MuiCardActionArea-focusHighlight': {
                            opacity: 0,
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.25 }}>
                        <Stack spacing={1.5}>
                          {/* Icono con fondo de color (~75 % respecto a 64 / 36 px) */}
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: informe.iconBg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <IconComponent
                              sx={{
                                fontSize: 27,
                                color: informe.color,
                              }}
                            />
                          </Box>

                          {/* Título */}
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              fontWeight: 600,
                              color: '#1a1a1a',
                              mb: 0.5
                            }}
                          >
                            {informe.title}
                          </Typography>

                          {/* Descripción */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.975rem',
                              lineHeight: 1.6,
                            }}
                          >
                            {informe.description}
                          </Typography>

                          {/* Indicador de acción */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: informe.color,
                              fontWeight: 500,
                              fontSize: '0.85rem',
                            }}
                          >
                            Ver informe
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                transition: 'transform 0.3s ease',
                                display: 'inline-block',
                                '&:hover': {
                                  transform: 'translateX(4px)',
                                }
                              }}
                            >
                              →
                            </Box>
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TesoreriaInformes;

